import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType, Redirect } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getCategoryPageData } from 'services/getCategoryPageData';
import { AttributeName, PageType, breakpoints } from '@storefront/lib-global';
import { CmsSlotsProvider } from '@storefront/lib-global/context/cmsSlotsContext';
import { MasterLayout } from '@storefront/lib-global/globalComponents/masterLayout';
import { Meta } from '@storefront/lib-global/globalComponents/seo/Meta';
import { initializeApollo } from '@storefront/lib-global/services/gql/apolloClient';
import { logger } from '@storefront/lib-global/utils/logger';
import { setNewRelicAttributes } from '@storefront/lib-global/utils/newrelic';
import { setCacheTagHeaderByType } from '@storefront/lib-global/utils/setCacheTagHeader';
import { setCustomCacheHeader } from '@storefront/lib-global/utils/setCustomCacheHeader';

/* eslint-disable promise/prefer-await-to-then */
const ProductCategory = dynamic(() =>
  import('components/productCategory/ProductCategory').then((mod) => mod.ProductCategory),
);
/* eslint-disable promise/prefer-await-to-then */

export default function CategoryPage({
  category,
  handlebarComponents,
  products,
  plpLayout,
  host,
  locale,
  serverUrl,
  cmsSlotsData,
  isMobileAgent,
}: InferGetServerSidePropsType<typeof getServerSideProps>): ReactElement {
  const { query } = useRouter();

  const [isMobile, setIsMobile] = useState(isMobileAgent);

  useEffect(() => {
    // Client-side check to ensure consistency
    setIsMobile(window.innerWidth < breakpoints.mobile);
  }, []);

  useEffect(() => {
    setNewRelicAttributes(
      {
        [AttributeName.CATEGORY_ID]: category.key,
        [AttributeName.CATEGORY_NAME]: category.name,
        [AttributeName.CATEGORY_PAGE_NUM]: query?.page ? +query.page : 1,
        [AttributeName.CATEGORY_RESULT_COUNT]: products.nbHits,
      },
      { interactionOnly: true },
    );
  }, [category, products, query]);

  return (
    <>
      <Meta
        {...category}
        isCategory
        host={host}
        nbHits={products?.nbHits}
        pageSize={products?.hitsPerPage}
        categoryKey={category?.key}
        pageType={PageType.PLP}
      />
      <MasterLayout>
        <CmsSlotsProvider cmsSlotsData={cmsSlotsData}>
          <ProductCategory
            plpLayout={plpLayout}
            category={category}
            handlebarComponents={handlebarComponents}
            locale={locale}
            serverUrl={serverUrl}
            products={products}
            isMobile={isMobile}
          />
        </CmsSlotsProvider>
      </MasterLayout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ query, locale, req, res }) => {
  const { host } = req.headers;

  // server-side device type set in middleware
  const userDevice = res.getHeaders()['user-device'] as string;

  const apolloClient = initializeApollo(req, res);

  const [{ notFound, props, redirect }, translations] = await Promise.all([
    getCategoryPageData(apolloClient, {
      locale,
      slug: query.slug as string[] | undefined,
      query,
    }),
    serverSideTranslations(locale || 'en', [
      'lib-content-common',
      'lib-global-common',
      'lib-products-common',
      'lib-appointments-common',
      'lib-csa-common',
      'lib-global-countries-common',
    ]),
  ]);
  logger.debug({ notFound, props, redirect }, 'getCategoryPageData');

  setCacheTagHeaderByType(res, locale, { category: props?.category }, query.slug as string[] | undefined);

  if (notFound) {
    return {
      notFound,
    };
  }
  if (redirect) {
    return {
      redirect: redirect as Redirect,
    };
  }

  const protocol = req.headers.referer?.split('://')[0] || 'https';
  const serverUrl = `${protocol}://${req.headers.host}${req.url}`;

  setCustomCacheHeader(res, { noStore: true, noCache: true, mustRevalidate: true });

  return {
    props: {
      ...props,
      ...translations,
      host,
      locale,
      serverUrl,
      isMobileAgent: userDevice === 'mobile',
    },
  };
};
